// Form primitives for the provider application, styled with the same tokens/cards
// as the patient funnel (see components/ui.tsx). Inline styles, as the design uses.
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { card } from '../components/ui';

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 800,
  color: 'var(--teal-deep)',
  textTransform: 'uppercase',
  letterSpacing: '.1em',
  marginBottom: 8,
};

const inputStyle: CSSProperties = {
  ...card,
  border: 'none',
  outline: 'none',
  width: '100%',
  padding: '14px 16px',
  fontSize: 16,
  fontWeight: 600,
  background: '#fff',
};

export function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: boolean;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <span style={labelStyle}>
        {label}
        {required && <span style={{ color: 'var(--peach)', marginLeft: 4 }}>*</span>}
      </span>
      {children}
      {hint && (
        <div className="pretty" style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 7, lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
      {error && (
        <div style={{ fontSize: 13, fontWeight: 700, color: '#C2554F', marginTop: 6 }}>This field is required.</div>
      )}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  inputMode,
  autoComplete,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: 'text' | 'tel' | 'numeric' | 'decimal' | 'email';
  autoComplete?: string;
  error?: boolean;
}) {
  return (
    <input
      type={type}
      inputMode={inputMode}
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inputStyle, boxShadow: error ? '0 0 0 2px #E8B4B0, var(--card-shadow)' : 'var(--card-shadow)' }}
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }}
    />
  );
}

// A grid of toggleable chips — the multi-select answer to SelectRow.
export function ChipMultiSelect({
  options,
  values,
  onChange,
  columns = 2,
  error,
}: {
  options: string[];
  values: string[];
  onChange: (next: string[]) => void;
  columns?: number;
  error?: boolean;
}) {
  const toggle = (v: string) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 8,
        ...(error ? { borderRadius: 12, boxShadow: '0 0 0 2px #E8B4B0' } : {}),
      }}
    >
      {options.map((o) => {
        const selected = values.includes(o);
        return (
          <button
            key={o}
            onClick={() => toggle(o)}
            aria-pressed={selected}
            style={{
              ...card,
              borderRadius: 10,
              background: selected ? 'var(--teal-tint)' : '#fff',
              boxShadow: selected ? '0 2px 14px rgba(33,172,190,.22)' : 'var(--card-shadow)',
              padding: '11px 12px',
              fontSize: 14,
              fontWeight: selected ? 800 : 700,
              color: selected ? 'var(--teal-deep)' : 'var(--ink)',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>{o}</span>
            {selected && (
              <svg width={16} height={16} viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }} aria-hidden>
                <circle cx={10} cy={10} r={10} fill="var(--teal)" />
                <path d="M5.8 10.2l2.8 2.8 5.6-5.8" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Free-entry tags: type, Enter/comma to add, tap × to remove.
export function TagInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  const add = () => {
    const v = text.trim().replace(/,$/, '');
    if (v && !values.includes(v)) onChange([...values, v]);
    setText('');
  };
  return (
    <div style={{ ...card, padding: '10px 12px' }}>
      {values.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {values.map((v) => (
            <span
              key={v}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--teal-tint)',
                color: 'var(--teal-deep)',
                fontSize: 13,
                fontWeight: 800,
                borderRadius: 8,
                padding: '6px 10px',
              }}
            >
              {v}
              <button
                onClick={() => onChange(values.filter((x) => x !== v))}
                aria-label={`Remove ${v}`}
                style={{ color: 'var(--teal-deep)', fontWeight: 800, fontSize: 14, lineHeight: 1, padding: 2 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
          if (e.key === 'Backspace' && !text && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={add}
        placeholder={placeholder}
        style={{ border: 'none', outline: 'none', width: '100%', fontSize: 15, fontWeight: 600, background: 'transparent', padding: '4px 2px' }}
      />
    </div>
  );
}

// Checkbox row in the funnel's consent style.
export function CheckRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
      />
      <span className="pretty" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.45 }}>
        {children}
      </span>
    </label>
  );
}

export { inputStyle };
